import { apiClient } from "./client";
import type { Project } from "./projects";

export interface TsangpoCampaignSpec {
  id: string;
  label: string;
  family: "continuous" | "gapped" | string;
  phase: "cruise" | "takeoff" | "landing" | string;
  geometry_variant_id: string;
  flow360_project_id: string;
  flow360_visibility: string;
  flow360_refresh_status: string;
  flow360_run_root?: string;
  legacy_project_id: string;
  legacy_parent_case_id?: string;
  legacy_visibility: string;
  baseline: Record<string, number>;
  sweeps: Record<string, number[]>;
  postprocess: string;
  planned_case_count: number;
  phase_detail?: Record<string, number | string>;
  geometry_variant?: {
    label: string;
    family: string;
    csm_asset: string;
    csm_source_name: string;
    despmtrs: Record<string, number>;
    surfaces: string[];
  };
}

export interface TsangpoProjectSpec {
  project: {
    id: string;
    name: string;
    description?: string;
    source_repo?: Record<string, string>;
  };
  campaigns: TsangpoCampaignSpec[];
  execution: {
    owner: string;
    flow360_auth: string;
    flow360_run_root?: string;
    repo_runtime_required: boolean;
    submit_adapter: string;
  };
}

export interface TsangpoSubmitResponse {
  status: "planned" | "queued";
  run_plan: {
    plan_id: string;
    status: string;
    operation_count: number;
    execution: Record<string, unknown>;
  };
  job?: {
    id: string;
    project_id: string;
    surface_id: string;
    campaign_id: string | null;
    status: string;
    plan_hash: string;
    idempotency_key: string;
    created_at?: string;
    updated_at?: string;
  };
}

export interface TsangpoBuildJob {
  id: string;
  project_id: string;
  surface_id: string;
  campaign_id: string | null;
  status: string;
  flow360_project_ids?: string[];
  flow360_case_ids?: string[];
  metadata?: Record<string, unknown>;
  updated_at?: string | null;
}

export interface TsangpoBuildJobsResponse {
  project_id: string;
  jobs: TsangpoBuildJob[];
}

export const TSANGPO_FALLBACK_SPEC: TsangpoProjectSpec = {
  project: {
    id: "tsangpo-qiqi-local",
    name: "Tsangpo geometry / Flow360 intake",
    description: "Thread-owned Tsangpo STOL geometry and Flow360 campaign model.",
    source_repo: {
      provider: "github",
      owner: "qiqi",
      name: "tsangpo",
      url: "https://github.com/qiqi/tsangpo",
      observed_commit: "d889d19ac431418b0aceab1148115bdff738248d",
      role: "source-lineage-only",
    },
  },
  execution: {
    owner: "thread",
    flow360_auth: "stored_user_api_key",
    flow360_run_root: "thread-prod-20260516T153357Z",
    repo_runtime_required: false,
    submit_adapter: "thread_flow360_csm_campaign_v1",
  },
  campaigns: [
    {
      id: "continuous-cruise",
      label: "Continuous cruise",
      family: "continuous",
      phase: "cruise",
      geometry_variant_id: "continuous_low_htail",
      flow360_project_id: "prj-dd063136-d535-4bd5-9251-ff81457055a8",
      flow360_visibility: "owned_by_current_user",
      flow360_refresh_status: "fresh",
      flow360_run_root: "thread-prod-20260516T153357Z",
      legacy_project_id: "prj-16082511-6d6a-447e-8c54-828d476c0a85",
      legacy_visibility: "not_visible_with_current_key",
      baseline: { alpha_deg: 7, theta_htail_deg: 0, thrust_mult: 1 },
      sweeps: {
        alpha_deg: [-3, -1, 1, 3, 5, 7, 9, 11, 13, 15],
        theta_htail_deg: [-12, -9, -6, -3, 0, 3, 6, 9, 12, 15],
        thrust_mult: [0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3],
      },
      postprocess: "thread://tsangpo/post/v2_continuous/cruise",
      planned_case_count: 31,
    },
    {
      id: "continuous-takeoff",
      label: "Continuous takeoff",
      family: "continuous",
      phase: "takeoff",
      geometry_variant_id: "continuous_low_htail",
      flow360_project_id: "prj-dd750a45-e5d8-473f-b60f-406a00255919",
      flow360_visibility: "owned_by_current_user",
      flow360_refresh_status: "fresh",
      flow360_run_root: "thread-prod-20260516T153357Z",
      legacy_project_id: "prj-9cd3ad10-ea47-41d9-9e33-0096fc30d6c1",
      legacy_visibility: "not_visible_with_current_key",
      baseline: { alpha_deg: 8, theta_htail_deg: -5, thrust_mult: 16 },
      sweeps: {
        alpha_deg: [-2, 2, 5, 8, 11, 14, 17, 20, 25, 30],
        theta_htail_deg: [-15, -10, -5, 0, 5, 10, 15, 20, 25, 30],
        thrust_mult: [6, 9, 12, 14, 16, 18, 20, 22, 25, 30],
      },
      postprocess: "thread://tsangpo/post/v2_continuous/takeoff",
      planned_case_count: 31,
    },
    {
      id: "continuous-landing",
      label: "Continuous landing",
      family: "continuous",
      phase: "landing",
      geometry_variant_id: "continuous_low_htail",
      flow360_project_id: "prj-04526159-36e0-4ba1-890a-bbd0ecef681e",
      flow360_visibility: "owned_by_current_user",
      flow360_refresh_status: "fresh",
      flow360_run_root: "thread-prod-20260516T153357Z",
      legacy_project_id: "prj-e7dc7d6d-4baf-4101-8818-1173da5a359a",
      legacy_visibility: "not_visible_with_current_key",
      baseline: { alpha_deg: 8, theta_htail_deg: -6, thrust_mult: 12 },
      sweeps: {
        alpha_deg: [-2, 2, 5, 8, 11, 14, 17, 20, 25, 30],
        theta_htail_deg: [-10, 0, 10, 15, 20, 25, 30, 35, 40, 50],
        thrust_mult: [4, 7, 10, 12, 14, 16, 18, 21, 25, 30],
      },
      postprocess: "thread://tsangpo/post/v2_continuous/landing",
      planned_case_count: 31,
    },
    {
      id: "gapped-cruise",
      label: "Gap40 cruise",
      family: "gapped",
      phase: "cruise",
      geometry_variant_id: "gapped_low_htail",
      flow360_project_id: "prj-58094ca0-77b3-490c-805f-65f1fbf8dd10",
      flow360_visibility: "owned_by_current_user",
      flow360_refresh_status: "warn",
      flow360_run_root: "thread-prod-20260516T153357Z",
      legacy_project_id: "prj-59c27343-3c39-43a4-ab19-18863acf02c4",
      legacy_parent_case_id: "case-3a4859d9-5298-4a35-8d68-b98bcb26b580",
      legacy_visibility: "not_visible_with_current_key",
      baseline: { alpha_deg: 7, theta_htail_deg: 0, thrust_mult: 1 },
      sweeps: {
        alpha_deg: [-3, -1, 1, 3, 5, 7, 9, 11, 13, 15],
        theta_htail_deg: [-12, -9, -6, -3, 3, 6, 9, 12, 15],
        thrust_mult: [0, 0.25, 0.5, 0.75, 1.25, 1.5, 2, 2.5, 3],
      },
      postprocess: "thread://tsangpo/post/v2_gapped/cruise",
      planned_case_count: 29,
    },
    {
      id: "gapped-takeoff",
      label: "Gap40 takeoff",
      family: "gapped",
      phase: "takeoff",
      geometry_variant_id: "gapped_low_htail",
      flow360_project_id: "prj-eca32f84-b0ea-44a8-9102-b6e52544c90b",
      flow360_visibility: "owned_by_current_user",
      flow360_refresh_status: "warn",
      flow360_run_root: "thread-prod-20260516T153357Z",
      legacy_project_id: "prj-e0e11ed5-2f3a-4e7d-8af7-c79baaf3ed22",
      legacy_parent_case_id: "case-fe40382d-13d5-4343-8626-453fd3872993",
      legacy_visibility: "not_visible_with_current_key",
      baseline: { alpha_deg: 8, theta_htail_deg: -5, thrust_mult: 16 },
      sweeps: {
        alpha_deg: [-2, 2, 5, 11, 14, 17, 20, 25, 30],
        theta_htail_deg: [-15, -10, 0, 5, 10, 15, 20, 25, 30],
        thrust_mult: [6, 9, 12, 14, 18, 20, 22, 25, 30],
      },
      postprocess: "thread://tsangpo/post/v2_gapped/takeoff",
      planned_case_count: 28,
    },
    {
      id: "gapped-landing",
      label: "Gap40 landing",
      family: "gapped",
      phase: "landing",
      geometry_variant_id: "gapped_low_htail",
      flow360_project_id: "prj-f1bd5e1a-3834-4f79-97ca-82d47d1b5685",
      flow360_visibility: "owned_by_current_user",
      flow360_refresh_status: "warn",
      flow360_run_root: "thread-prod-20260516T153357Z",
      legacy_project_id: "prj-0cd29981-d281-442a-984f-06562abc1f39",
      legacy_parent_case_id: "case-3fe6a528-9151-4337-8ece-8f0862fee1bf",
      legacy_visibility: "not_visible_with_current_key",
      baseline: { alpha_deg: 8, theta_htail_deg: -6, thrust_mult: 12 },
      sweeps: {
        alpha_deg: [-2, 2, 5, 11, 14, 17, 20, 25, 30],
        theta_htail_deg: [-10, 0, 10, 15, 20, 25, 30, 35, 40, 50],
        thrust_mult: [4, 7, 10, 14, 16, 18, 21, 25, 30],
      },
      postprocess: "thread://tsangpo/post/v2_gapped/landing",
      planned_case_count: 29,
    },
  ],
};

export function fetchTsangpoProjectSpec(): Promise<TsangpoProjectSpec> {
  return apiClient<TsangpoProjectSpec>("/tsangpo/project");
}

export function ensureTsangpoThreadProject(): Promise<Project> {
  return apiClient<Project>("/tsangpo/project/ensure", {
    method: "POST",
  });
}

export function planTsangpoCampaign(
  campaignId: string,
  overrides?: Record<string, unknown>,
  projectId?: string,
): Promise<TsangpoSubmitResponse> {
  return apiClient<TsangpoSubmitResponse>(`/tsangpo/campaigns/${encodeURIComponent(campaignId)}/submit`, {
    method: "POST",
    body: { execute: false, overrides, project_id: projectId },
  });
}

export function fetchTsangpoBuildJobs(projectId: string): Promise<TsangpoBuildJobsResponse> {
  return apiClient<TsangpoBuildJobsResponse>(`/projects/${encodeURIComponent(projectId)}/build-jobs?limit=200`);
}
